/**
 * «Borrar» una planificación o una cirugía en cualquier momento (también ya firmada): sale de la lista y del odontograma, pero queda en el
 * registro con quién la borró, cuándo y por qué. Lo firmado (cirugía, controles, sesiones, notas de evolución) no se destruye.
 */
import { useState } from "react";
import { api, puede } from "../api.ts";
import { AvisoError, useAccion } from "../pantallas/comun.tsx";

export interface CasoBorrado { id: string; titulo: string; anulado_en: string | null; motivo_anulacion: string; anulado_por: string | null }

export function BorrarCaso<T>({ url, que, firmado, alBorrado }: { url: string; que: string; firmado: boolean; alBorrado: (resultado: T) => void }) {
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const a = useAccion();
  if (!puede("clinico:escribir") || (firmado && !puede("clinico:firmar"))) return null;
  return (
    <div style={{ display: "grid", gap: 6 }}>
      {!abierto
        ? <div><button type="button" className="btn chico peligro" onClick={() => setAbierto(true)} title="Sale de la lista y del odontograma, pero queda en el registro">Borrar {que}</button></div>
        : (
          <div className="panel-accion" role="group" aria-label={`Borrar ${que}`}>
            <b>Borrar {que}</b>
            <p className="muted" style={{ margin: "2px 0 6px", fontSize: 13 }}>
              {firmado ? "Aunque ya esté firmado, se puede borrar. " : ""}Sale de la lista y se quitan sus marcas del odontograma, pero <b>queda guardado en el registro</b> con tu nombre, la fecha y el motivo
              {firmado ? "; lo que ya se firmó (cirugía, controles, notas de evolución) no se destruye" : ""}.
            </p>
            <label className="campo">Motivo (obligatorio)<input className="entrada" value={motivo} maxLength={300} placeholder="Ej.: se planificó en la pieza equivocada" onChange={(e) => setMotivo(e.target.value)} autoFocus /></label>
            <AvisoError error={a.error} />
            <div className="fila">
              <button type="button" className="btn peligro" disabled={a.ocupado || motivo.trim().length < 5} onClick={async () => { const r = await a.correr(() => api<T>("POST", url, { motivo })); if (r) { setAbierto(false); setMotivo(""); alBorrado(r); } }}>Borrar y dejar en el registro</button>
              <button type="button" className="btn" onClick={() => setAbierto(false)}>Cancelar</button>
            </div>
          </div>
        )}
    </div>
  );
}

/** Los casos ya borrados: solo lectura, para que el registro se vea. */
export function CasosBorrados({ casos }: { casos: CasoBorrado[] }) {
  if (!casos.length) return null;
  const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("es-EC", { day: "numeric", month: "short", year: "numeric" }) : "");
  return (
    <details>
      <summary style={{ cursor: "pointer", fontSize: 14 }}>Borrados ({casos.length}) · quedan en el registro</summary>
      <ul className="lista" style={{ marginTop: 6 }}>
        {casos.map((c) => (
          <li key={c.id} style={{ display: "grid", gap: 2, padding: "6px 4px", opacity: 0.85 }}>
            <span><b>{c.titulo}</b></span>
            <span className="muted" style={{ fontSize: 13 }}>Borrado el {fecha(c.anulado_en)}{c.anulado_por ? ` por ${c.anulado_por}` : ""}. Motivo: {c.motivo_anulacion}</span>
          </li>
        ))}
      </ul>
    </details>
  );
}
